import React, { useState, useEffect } from 'react';
import { FiGlobe, FiUpload, FiSearch, FiEdit, FiCheckCircle, FiArrowRight, FiX } from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import './Onboarding.css';

const EMPTY_UPLOAD = { files: [], outcome: 'funded', score: '', feedback: '', feedbackFile: null, nofoUrl: '', nofoFile: null };

const Onboarding = ({ user, onComplete, onNavigate }) => {
  const [step, setStep] = useState(1);
  const [seedUrl, setSeedUrl] = useState('');
  const [seedName, setSeedName] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [uploadedList, setUploadedList] = useState([]);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({ ...EMPTY_UPLOAD });

  // Check if profile already has data
  useEffect(() => {
    fetch(getApiEndpoint('/api/grant-proposals/pi-profile'), { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.pi_name) {
          setProfileLoaded(true);
          setStep(2);
          if (data.style_fingerprint) setStep(3);
        }
      })
      .catch(() => {});
  }, []);

  const seedProfile = async () => {
    if (!seedUrl.trim() && !seedName.trim()) return;
    setSeeding(true);
    try {
      const res = await fetch(getApiEndpoint('/api/grant-proposals/seed-profile'), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: seedUrl.trim(), name: seedName.trim() || null }),
      });
      if (res.ok) {
        const data = await res.json();
        setSeedResult(data);
        setProfileLoaded(true);
        setStep(2);
      }
    } catch (_) {}
    setSeeding(false);
  };

  const submitUpload = async () => {
    if (!uploadForm.files.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      uploadForm.files.forEach(f => formData.append('files', f));
      formData.append('funding_outcome', uploadForm.outcome);
      if (uploadForm.score) formData.append('priority_score', uploadForm.score);
      if (uploadForm.feedback) formData.append('reviewer_feedback', uploadForm.feedback);
      if (uploadForm.feedbackFile) formData.append('feedback_file', uploadForm.feedbackFile);
      if (uploadForm.nofoUrl) formData.append('nofo_url', uploadForm.nofoUrl);
      if (uploadForm.nofoFile) formData.append('nofo_file', uploadForm.nofoFile);
      const res = await fetch(getApiEndpoint('/api/grant-proposals/upload-winning-proposal'), {
        method: 'POST', headers: getAuthHeaders(), body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setUploadCount(prev => prev + 1);
        setUploadedList(prev => [...prev, { title: data.title, outcome: data.funding_outcome }]);
        setShowUploadModal(false);
        setUploadForm({ ...EMPTY_UPLOAD });
      }
    } catch (_) {}
    setUploading(false);
  };

  return (
    <div className="ob-page">
      <div className="ob-header">
        <h1>Welcome to Mnevia</h1>
        <p>Let's set up your research copilot in a few minutes.</p>
      </div>

      {/* Progress bar */}
      <div className="ob-progress">
        {[1, 2, 3, 4].map(s => (
          <div key={s} className={`ob-progress-step ${step >= s ? 'ob-progress-step--done' : ''} ${step === s ? 'ob-progress-step--active' : ''}`}>
            {step > s ? <FiCheckCircle /> : s}
          </div>
        ))}
        <div className="ob-progress-bar">
          <div className="ob-progress-fill" style={{ width: `${((step - 1) / 3) * 100}%` }} />
        </div>
      </div>

      {/* Step 1: Seed profile */}
      {step === 1 && (
        <div className="ob-card">
          <div className="ob-card-icon"><FiGlobe /></div>
          <h2>Build your research identity</h2>
          <p>Paste your faculty page URL. We'll import your papers, grants, and expertise automatically.</p>
          <div className="ob-form">
            <input
              className="ob-input"
              value={seedUrl}
              onChange={e => setSeedUrl(e.target.value)}
              placeholder="https://faculty.university.edu/yourname"
              disabled={seeding}
            />
            <input
              className="ob-input ob-input--sm"
              value={seedName}
              onChange={e => setSeedName(e.target.value)}
              placeholder="Your full name (helps if page is dynamic)"
              disabled={seeding}
              onKeyDown={e => e.key === 'Enter' && seedProfile()}
            />
            <button className="ob-btn ob-btn--primary" onClick={seedProfile} disabled={seeding || (!seedUrl.trim() && !seedName.trim())}>
              {seeding ? 'Importing...' : 'Import My Profile'}
            </button>
          </div>
          {seedResult && (
            <div className="ob-result">
              <FiCheckCircle /> {seedResult.profile?.pi_name || 'Profile'} — {seedResult.paper_count || 0} papers imported
            </div>
          )}
          <button className="ob-skip" onClick={() => setStep(2)}>Skip for now</button>
        </div>
      )}

      {/* Step 2: Upload proposals */}
      {step === 2 && (
        <div className="ob-card">
          <div className="ob-card-icon"><FiUpload /></div>
          <h2>Teach it your writing voice</h2>
          <p>Upload 2-3 proposal PDFs with their outcomes and reviewer feedback. The system learns your framing, tone, and what gets funded.</p>
          <div className="ob-upload-area">
            <button className="ob-upload-btn" onClick={() => setShowUploadModal(true)} disabled={uploading}>
              <FiUpload /> {uploading ? 'Uploading...' : 'Upload a Proposal (PDF)'}
            </button>
            {uploadedList.length > 0 && (
              <div className="ob-uploaded-list">
                {uploadedList.map((u, i) => (
                  <div key={i} className="ob-uploaded-item">
                    <FiCheckCircle className="ob-uploaded-icon" />
                    <span className="ob-uploaded-title">{u.title}</span>
                    <span className={`ob-uploaded-chip ob-uploaded-chip--${u.outcome}`}>{u.outcome}</span>
                  </div>
                ))}
              </div>
            )}
            {uploadCount > 0 && (
              <div className="ob-result">
                <FiCheckCircle /> {uploadCount} proposal{uploadCount !== 1 ? 's' : ''} uploaded
                {uploadCount >= 3 && ' — style fingerprint generating!'}
              </div>
            )}
          </div>
          <p className="ob-hint">Upload 3+ to unlock your writing style fingerprint. Include rejected proposals with reviewer feedback to learn win patterns.</p>
          <div className="ob-nav">
            <button className="ob-btn ob-btn--outline" onClick={() => setStep(1)}>Back</button>
            <button className="ob-btn ob-btn--primary" onClick={() => setStep(3)}>
              {uploadCount > 0 ? 'Next' : 'Skip for now'} <FiArrowRight />
            </button>
          </div>

          {/* Upload modal */}
          {showUploadModal && (
            <div className="ob-modal-overlay" onClick={() => !uploading && setShowUploadModal(false)}>
              <div className="ob-modal" onClick={e => e.stopPropagation()}>
                <div className="ob-modal-header">
                  <h3><FiUpload /> Upload Proposal</h3>
                  <button className="ob-modal-close" onClick={() => !uploading && setShowUploadModal(false)}><FiX /></button>
                </div>
                <div className="ob-modal-body">
                  <div className="ob-field">
                    <label>Proposal PDFs <span className="ob-required">*</span></label>
                    <input type="file" accept=".pdf" multiple
                      onChange={e => setUploadForm(f => ({ ...f, files: Array.from(e.target.files || []) }))} />
                    <span className="ob-hint-text">Select one or more PDFs (e.g., cover + summary + description)</span>
                  </div>
                  <div className="ob-field">
                    <label>Outcome</label>
                    <select value={uploadForm.outcome}
                      onChange={e => setUploadForm(f => ({ ...f, outcome: e.target.value }))}>
                      <option value="funded">Funded</option>
                      <option value="rejected">Rejected</option>
                      <option value="pending">Pending</option>
                      <option value="withdrawn">Withdrawn</option>
                    </select>
                  </div>
                  <div className="ob-field">
                    <label>Priority Score <span className="ob-hint-text">(NIH 1–9 — optional)</span></label>
                    <input type="number" min="1" max="9" placeholder="e.g., 3"
                      value={uploadForm.score}
                      onChange={e => setUploadForm(f => ({ ...f, score: e.target.value }))} />
                  </div>
                  <div className="ob-field">
                    <label>NOFO / RFP <span className="ob-hint-text">(optional — PDF or URL)</span></label>
                    <input type="file" accept=".pdf"
                      onChange={e => setUploadForm(f => ({ ...f, nofoFile: e.target.files?.[0] || null, nofoUrl: '' }))} />
                    <span className="ob-or">or paste URL:</span>
                    <input type="url" placeholder="https://grants.nih.gov/..."
                      value={uploadForm.nofoUrl}
                      onChange={e => setUploadForm(f => ({ ...f, nofoUrl: e.target.value, nofoFile: null }))} />
                  </div>
                  <div className="ob-field">
                    <label>Reviewer Feedback <span className="ob-hint-text">(optional — paste text or upload PDF/DOCX)</span></label>
                    <input type="file" accept=".pdf,.docx"
                      onChange={e => setUploadForm(f => ({ ...f, feedbackFile: e.target.files?.[0] || null }))} />
                    <span className="ob-or">or paste text:</span>
                    <textarea rows={4} placeholder="Paste reviewer comments or summary statement..."
                      value={uploadForm.feedback}
                      onChange={e => setUploadForm(f => ({ ...f, feedback: e.target.value }))} />
                  </div>
                </div>
                <div className="ob-modal-footer">
                  <button className="ob-btn ob-btn--outline" onClick={() => setShowUploadModal(false)} disabled={uploading}>Cancel</button>
                  <button className="ob-btn ob-btn--primary" onClick={submitUpload}
                    disabled={!uploadForm.files.length || uploading}>
                    <FiUpload /> {uploading ? 'Parsing & Importing...' : 'Upload & Import'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Explore */}
      {step === 3 && (
        <div className="ob-card">
          <div className="ob-card-icon"><FiSearch /></div>
          <h2>See your research white space</h2>
          <p>Your papers are imported. Explore where your expertise is unique and what ideas are worth pursuing.</p>
          <div className="ob-actions">
            <button className="ob-btn ob-btn--primary" onClick={() => { onNavigate('collections'); }}>
              <FiSearch /> Explore My Collections
            </button>
          </div>
          <div className="ob-nav">
            <button className="ob-btn ob-btn--outline" onClick={() => setStep(2)}>Back</button>
            <button className="ob-btn ob-btn--outline" onClick={() => setStep(4)}>
              Skip to dashboard <FiArrowRight />
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Ready */}
      {step === 4 && (
        <div className="ob-card">
          <div className="ob-card-icon"><FiEdit /></div>
          <h2>You're ready to draft</h2>
          <p>Every draft is grounded in your evidence and styled to your voice. After drafting, a six-reviewer panel calibrated to your grant type flags weaknesses — accept the ones you agree with, click Iterate Draft, and the system rewrites the flagged sections with real citations. Then a separate compliance panel checks NOFO requirements before submission.</p>
          <div className="ob-actions">
            <button className="ob-btn ob-btn--primary" onClick={() => onNavigate('grant-agent')}>
              Draft a Proposal
            </button>
            <button className="ob-btn ob-btn--outline" onClick={() => onNavigate('paper-agent')}>
              Draft a Paper
            </button>
          </div>
          <button className="ob-skip" onClick={onComplete}>Go to Dashboard</button>
        </div>
      )}
    </div>
  );
};

export default Onboarding;
